import { ClassifierData } from 'types/classifiers';
import {
    Effort,
    EffortDto,
    EffortForm,
    EffortPartyRoleType,
    NominationLine,
    NominationLineForm,
    NominationReferee,
    RANKINGS_REFEREE_TYPE,
} from 'types/efforts';
import { NamedEntityMinDto } from 'types/other';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getClassifierOptionByCode, getOptionsFromClassifier } from 'utils/formUtil';
import { sortByField } from 'utils/sort';
import { secondsToHours } from 'utils/time';

/**
 * The classifiers the effort form reads: the activity types, the expense types and currencies of its expenses, and
 * the legal directories of its nominations.
 */
export const EFFORT_FORM_CLASSIFIERS = ['EFFORT_ACTIVITY_TYPE', 'EXPENSE_TYPE', 'CURRENCY', 'LEGAL_DIRECTORY'];

const partiesOfRole = (effort: Effort, role: EffortPartyRoleType) =>
    sortByField(
        (effort.parties || [])
            .filter((effortParty) => effortParty.effortPartyRoleTypeCode === role)
            .map((effortParty) => ({ id: effortParty.party.id, name: effortParty.party.name })),
        'name'
    );

/** The effort form of a stored effort. Its expenses go as stored, the list the expenses section edits. */
export const effortDtoToForm = (effort: Effort, classifiers: Record<string, ClassifierData>): EffortForm => ({
    id: effort.id ?? null,
    description: effort.description ?? '',
    effortDate: effort.effortDate ? formatDateStringInput(new Date(effort.effortDate)) : null,
    effortByEmployee: effort.effortByEmployee?.id
        ? { id: effort.effortByEmployee.id, name: effort.effortByEmployee.name }
        : null,
    usedTimeInSeconds: effort.usedTimeInSeconds ?? null,
    // A draft may have no time yet, which the field shows empty.
    usedTimeInHours: effort.usedTimeInSeconds == null ? '' : String(secondsToHours(effort.usedTimeInSeconds, 2)),
    effortActivityType:
        (getOptionsFromClassifier(classifiers['EFFORT_ACTIVITY_TYPE']) as { label: string; value: string }[]).find(
            (val) => val.value === effort?.effortActivityTypeCode
        ) ?? null,
    employees: sortByField(
        (effort.employees || []).map((effortEmployee) => ({
            id: effortEmployee.employee.id,
            name: effortEmployee.employee.name,
        })),
        'name'
    ),
    matters: (effort.matters || []).map((effortMatter) => ({
        id: effortMatter.matter.id,
        fullName: effortMatter.matter.fullName,
        isConfidential: effortMatter.matter.isConfidential,
    })),
    partyPersons: partiesOfRole(effort, EffortPartyRoleType.FOR_PERSONS),
    partyCompanies: partiesOfRole(effort, EffortPartyRoleType.FOR_ENTITIES),
    expenses: effort.expenses || [],
    nominations: effort.nominations?.map(({ legalDirectoryCode, ...line }) => ({
        ...line,
        legalDirectory: getClassifierOptionByCode<string, string>(classifiers['LEGAL_DIRECTORY'], legalDirectoryCode),
    })),
    lockedRefereePartyIds: effort.lockedRefereePartyIds ?? undefined,
});

/** A form line as the API takes it: the directory's code, the edition as a number. */
export const nominationLineToRequest = ({
    legalDirectory,
    editionYear,
    ...line
}: NominationLineForm): NominationLine => ({
    ...line,
    legalDirectoryCode: legalDirectory?.value ?? null,
    editionYear: editionYear === null || editionYear === '' ? null : Number(editionYear),
    referees: line.referees.map(({ id, party, statusCode }) => ({
        id,
        party: { id: party.id, name: party.name },
        statusCode,
    })),
});

/**
 * The indexes of the lines that name the same directory, edition and practice area as another. As the server compares
 * them: only a line with a referee counts, and empty fields match.
 */
export const duplicateNominationLines = (lines: NominationLineForm[]): Set<number> => {
    const keys = lines.map((line) => {
        if (line.referees.length === 0) return null;
        const { legalDirectoryCode, editionYear } = nominationLineToRequest(line);
        return JSON.stringify([legalDirectoryCode, editionYear, line.practiceArea?.id ?? null]);
    });
    return new Set(
        keys.flatMap((key, index) => (key !== null && keys.indexOf(key) !== keys.lastIndexOf(key) ? [index] : []))
    );
};

/**
 * The lines the effort keeps: none once its type is another, and only the referees still in Effort for people, so
 * a section that changes the type or the people sends nominations the server accepts. Absent stays absent.
 */
const keptNominations = (form: EffortForm): NominationLine[] | undefined => {
    if (!form.nominations) return undefined;
    if (form.effortActivityType?.value !== RANKINGS_REFEREE_TYPE) return [];
    const people = new Set(form.partyPersons.map(({ id }) => id));
    return form.nominations
        .map((line) => ({ ...line, referees: line.referees.filter(({ party }) => people.has(party.id)) }))
        .filter((line) => line.referees.length > 0)
        .map(nominationLineToRequest);
};

/**
 * The create or update request of the effort form. The server upserts the expenses it is sent and leaves the
 * others alone, so an empty list changes none.
 */
export const effortFormToRequest = (form: EffortForm): EffortDto => ({
    id: form.id,
    effortActivityTypeCode: form.effortActivityType ? form.effortActivityType.value : null,
    description: form.description,
    effortDate: form.effortDate,
    usedTimeInSeconds: form.usedTimeInSeconds,
    effortByEmployee: form.effortByEmployee,
    matters: form.matters,
    employees: form.employees,
    partyPersons: form.partyPersons,
    partyCompanies: form.partyCompanies,
    expenses: (form.expenses || []).map((expense) => ({
        ...expense,
        expenseDateUtcTimestamp: expense?.expenseDate ? new Date(`${expense.expenseDate}T00:00:00`).toISOString() : '',
    })),
    nominations: keptNominations(form),
});

/** A person put forward on a line who has not answered yet. */
const requestedReferee = (party: NamedEntityMinDto): NominationReferee => ({
    party: { id: party.id, name: party.name },
    statusCode: 'REQUESTED',
});

/** The lines with each person added put on every line they are not on yet, as Requested. */
export const joinReferees = (lines: NominationLineForm[], added: NamedEntityMinDto[]): NominationLineForm[] =>
    lines.map((line) => ({
        ...line,
        referees: [
            ...line.referees,
            ...added.filter(({ id }) => !line.referees.some(({ party }) => party.id === id)).map(requestedReferee),
        ],
    }));

/**
 * The lines as stored now, joined by the people a section added to Effort for people: a status changed on the page
 * while the section was open stays as it was saved.
 */
export const joinAddedPeople = (edited: EffortForm, latest: EffortForm): NominationLineForm[] | undefined => {
    const before = new Set(latest.partyPersons.map(({ id }) => id));
    return (
        edited.nominations &&
        joinReferees(
            edited.nominations,
            edited.partyPersons.filter(({ id }) => !before.has(id))
        )
    );
};

/**
 * A new nomination line: everyone in Effort for people with the status REQUESTED, and the edition after the effort
 * date's year, so an effort dated in December and logged in January still defaults to the year after its own.
 */
export const newNominationLine = (effortDate: string | null, people: NamedEntityMinDto[]): NominationLineForm => ({
    legalDirectory: null,
    editionYear: effortDate ? Number(effortDate.slice(0, 4)) + 1 : null,
    practiceArea: null,
    referees: people.map(requestedReferee),
});

/** The form fields each section of the effort page edits. The used time is entered in hours and saved in seconds. */
export const EFFORT_SECTION_FIELDS = {
    overview: ['effortDate', 'usedTimeInHours', 'usedTimeInSeconds', 'effortActivityType', 'description'],
    'linked-entities': ['matters', 'partyPersons', 'partyCompanies'],
    people: ['effortByEmployee', 'employees'],
    expenses: ['expenses'],
    nominations: ['nominations'],
} satisfies Record<string, (keyof EffortForm)[]>;

export type EffortSectionId = keyof typeof EFFORT_SECTION_FIELDS;

/**
 * A section save's request. Only the Expenses section sends expense lines: the server upserts what it is sent, so
 * the other sections send none rather than rewrite every stored expense.
 */
export const effortSectionToRequest = (section: EffortSectionId, form: EffortForm): EffortDto =>
    effortFormToRequest(section === 'expenses' ? form : { ...form, expenses: [] });
